/**
 * Tasarım sistemi kataloğu (Tasarım V4, Öneri 12).
 *
 * `#/tasarim` rotasında renk, tipografi, boşluk, yarıçap, gölge, buton,
 * rozet ve kart örneklerini **canlı** gösterir. Amaç, `docs/` raporlarındaki
 * kuralları tarayıcıda tek sayfada görebilmek; yeni bir bileşen eklerken
 * hangi belirtecin kullanılacağını tahmin etmek zorunda kalmamak.
 *
 * Katalog **üretimde de** çalışır (ayrı bir bayrak yok): yalnızca bir rota,
 * veri çekmez, kimseyi etkilemez.
 */
import type { ReactNode } from 'react';
import { ItemCard } from '../components/ItemCard.tsx';
import { ArrowLeftIcon, BookmarkIcon } from '../Icon.tsx';

const COLORS = [
  { token: '--surface-0', label: 'Zemin', hex: '#0a0d12' },
  { token: '--surface-1', label: 'Sayfa', hex: '#0e1116' },
  { token: '--surface-2', label: 'Kart / panel', hex: '#161b22' },
  { token: '--surface-3', label: 'Yükseltilmiş', hex: '#21262d' },
  { token: '--accent', label: 'Vurgu (metin/ikon)', hex: '#7aa2f7' },
  { token: '--accent-strong', label: 'Vurgu (dolu)', hex: '#3b5bdb' },
  { token: '--accent-soft', label: 'Vurgu (yüzey)', hex: 'rgba(122,162,247,.14)' },
  { token: '--accent-text', label: 'Vurgu metni', hex: '#a8c0f0' },
  { token: '--danger', label: 'Tehlike', hex: '#ff6b6b' },
  { token: '--warn', label: 'Uyarı', hex: '#ffb454' },
  { token: '--success', label: 'Başarı', hex: '#4ade80' },
] as const;

const TYPE_SCALE = [
  { token: '--fs-2xs', label: '2xs · 11 px · rozet' },
  { token: '--fs-xs', label: 'xs · 12 px · meta' },
  { token: '--fs-sm', label: 'sm · 14 px · ikincil gövde' },
  { token: '--fs-md', label: 'md · 16 px · gövde' },
  { token: '--fs-lg', label: 'lg · 20 px · bölüm başlığı' },
  { token: '--fs-xl', label: 'xl · 24 px · ekran başlığı' },
  { token: '--fs-2xl', label: '2xl · clamp · kütüphane başlığı' },
  { token: '--fs-hero', label: 'hero · clamp · kahraman' },
] as const;

const SPACES = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const;
const RADII = [
  { token: '--radius-sm', label: 'sm · 6' },
  { token: '--radius-md', label: 'md · 10' },
  { token: '--radius-lg', label: 'lg · 16' },
  { token: '--radius-pill', label: 'pill' },
] as const;
const SHADOWS = ['1', '2', '3'] as const;

const DEMO_ITEM = {
  id: 'design-demo',
  libraryId: 'design',
  kind: 'movie',
  title: 'Örnek İçerik',
  year: 2026,
  seasonNumber: null,
  episodeNumber: null,
  durationMs: 7_320_000,
  progress: { positionMs: 2_400_000, state: 'playing' },
  isWatched: false,
} as const;

export function DesignSystemScreen(): ReactNode {
  return (
    <div className="design-page">
      <header className="design-header">
        <a className="link" href="#/">
          <ArrowLeftIcon size={16} />
          Ana Sayfa
        </a>
        <h1 className="design-title">Tasarım Sistemi</h1>
        <p className="design-lead">
          Renk, tipografi, boşluk, yarıçap ve bileşenlerin tek sayfalık canlı kataloğu. Yeni bir
          ekran eklerken buradaki belirteçleri kullanın; gömülü değer yazmayın.
        </p>
      </header>

      <Section title="Renk" note="Yüzeyler, tek vurgu ailesi ve durum renkleri.">
        <div className="design-swatches">
          {COLORS.map((color) => (
            <div className="design-swatch" key={color.token}>
              <div className="design-swatch__color" style={{ background: `var(${color.token})` }} />
              <div className="design-swatch__label">
                <code>{color.token}</code>
                <span>{color.label}</span>
                <span className="design-muted">{color.hex}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Tipografi" note="8 kademe; başlıklar clamp ile büyür, gövde sabit.">
        <div className="design-type-list">
          {TYPE_SCALE.map((type) => (
            <div className="design-type-row" key={type.token}>
              <code className="design-type-row__token">{type.token}</code>
              <span style={{ fontSize: `var(${type.token})`, lineHeight: 'var(--lh-tight)' }}>
                Kütüphanenizde 10 içerik var
              </span>
              <span className="design-muted design-type-row__label">{type.label}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section
        title="Boşluk"
        note="4 px tabanlı 9 kademe. 20 px gibi ara değerler yerine komşu kademe."
      >
        <div className="design-space-list">
          {SPACES.map((step) => (
            <div className="design-space-row" key={step}>
              <code className="design-type-row__token">--space-{step}</code>
              <span
                className="design-space-bar"
                style={{ width: `var(--space-${step})` }}
                aria-hidden="true"
              />
              <span className="design-muted">{`var(--space-${step})`}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Yarıçap" note="Yalnız dört değer: sm / md / lg / pill.">
        <div className="design-radius-row">
          {RADII.map((radius) => (
            <div
              className="design-radius-box"
              key={radius.token}
              style={{ borderRadius: `var(${radius.token})` }}
            >
              {radius.label}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Gölge" note="Yükselti tek başına değil, yüzeyle birlikte kullanılır.">
        <div className="design-shadow-row">
          {SHADOWS.map((level) => (
            <div
              className="design-shadow-box"
              key={level}
              style={{ boxShadow: `var(--shadow-${level})` }}
            >
              shadow-{level}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Butonlar" note="Bir görünümde en fazla bir dolu birincil eylem.">
        <div className="design-button-row">
          <button type="button" className="button button--primary">
            Birincil
          </button>
          <button type="button" className="button">
            İkincil
          </button>
          <button type="button" className="button button--ghost">
            Hayalet
          </button>
          <button type="button" className="button button--ghost" style={{ color: 'var(--danger)' }}>
            Tehlike (ikincil)
          </button>
        </div>
      </Section>

      <Section title="Rozetler" note="Durum renkleri yalnız durum taşır; kimlik rengiyle karışmaz.">
        <div className="design-badge-row">
          <span className="badge">Nötr</span>
          <span className="badge badge--watched">İzlendi</span>
          <span className="badge badge--warn">Uyarı</span>
          <span className="badge badge--down">Erişilemiyor</span>
          <span className="badge badge--cold">
            <BookmarkIcon size={12} />
            Tünel
          </span>
        </div>
      </Section>

      <Section
        title="Kart"
        note="Kapak, iki satır başlık ve tek satır meta. Kart başına tek ilerleme göstergesi."
      >
        <div className="design-card-row">
          <div style={{ width: 200 }}>
            <ItemCard item={DEMO_ITEM} href="#/tasarim" />
          </div>
          <div style={{ width: 200 }}>
            <ItemCard
              item={{ ...DEMO_ITEM, id: 'design-demo-2', isWatched: true }}
              href="#/tasarim"
            />
          </div>
        </div>
      </Section>

      <Section title="Odak halkası" note="Klavyeyle gezinmede her etkileşimli öğede görünür.">
        <div className="design-button-row">
          <button type="button" className="button">
            Tab ile odakla
          </button>
          <span className="design-muted">:focus-visible halkası: 2 px accent</span>
        </div>
      </Section>
    </div>
  );
}

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note: string;
  children: ReactNode;
}): ReactNode {
  return (
    <section className="design-section">
      <div className="design-section__header">
        <h2 className="design-section__title">{title}</h2>
        <p className="design-section__note">{note}</p>
      </div>
      {children}
    </section>
  );
}
